import { clsx } from 'clsx'
import { SECTIONS, UPLOAD_CATEGORIES, emptyField, guessUploadCategory, newId, type AssetsSection as Doc, type PortalConfig, type UploadCategory } from '@sonder/shared'
import { useEffect, useRef, useState } from 'react'
import { useMode } from '@/app/mode'
import { useAuth } from '@/auth/AuthProvider'
import { downloadUrl, useUploads } from '@/data/uploads'
import { useSectionEditor } from '@/data/editor'
import { Button, Eyebrow, Icons, Notice, Select, UploadedFileRow, Uploader } from '@/ui'
import { AddRow, NoEntities, useBrands } from './common'
import { FieldEditor } from './FieldEditor'

const F = Object.fromEntries(SECTIONS.assets.fields.map((f) => [f.key, f]))

export function AssetsSection({ clientId }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'assets')
  const brands = useBrands(clientId)
  const mode = useMode()
  const doc = ed.doc
  if (!doc) return null
  const driveIds = Object.keys(doc.drives ?? {})

  const addDrive = () => {
    const id = newId()
    const now = Date.now()
    ed.setRaw(`drives.${id}`, { label: emptyField('', now), url: emptyField('', now), access: emptyField('', now) })
  }

  return (
    <div className="grid gap-12">
      <section className="grid gap-6 border-t border-ink pt-6" data-field="drives">
        <div className="grid gap-1">
          <Eyebrow tone="muted">Folders</Eyebrow>
          <h2 className="text-h3">Existing photos, video and files</h2>
        </div>
        {driveIds.length === 0 && <NoEntities what="folders" hint="Add a Google Drive, Dropbox or gallery link and tell us how to get in." />}
        {driveIds.map((id, i) => (
          <div key={id} className="grid max-w-form gap-5 border-l-2 border-gray-200 pl-5">
            <div className="flex items-center justify-between">
              <span className="text-eyebrow uppercase tracking-eyebrow text-dim">Folder {i + 1}</span>
              {mode !== 'preview' && (
                <Button variant="danger-link" size="sm" arrow={false} onClick={() => ed.remove(`drives.${id}`)}>
                  Remove
                </Button>
              )}
            </div>
            <FieldEditor editor={ed} path={`drives.${id}.label`} def={F.label!} />
            <FieldEditor editor={ed} path={`drives.${id}.url`} def={F.url!} />
            <FieldEditor editor={ed} path={`drives.${id}.access`} def={F.access!} />
          </div>
        ))}
        {mode !== 'preview' && <AddRow label="Add folder" onAdd={addDrive} />}
        <Notice>Give hello@sondermediahouse.com upload access so we can add new shoots to the same place.</Notice>
      </section>

      <UploadsBlock clientId={clientId} uploads={doc.uploads ?? {}} ed={ed} brands={brands} />
    </div>
  )
}

/** What we hope to find, ticked off as matching files arrive. Folder contents count too; we just can't see them here. */
const LOOK_FOR: { category: UploadCategory; label: string }[] = [
  { category: 'logo', label: 'Logos (vector or transparent PNG)' },
  { category: 'font', label: 'Fonts' },
  { category: 'guide', label: 'Style guide or brand colors' },
  { category: 'video', label: 'Video intros or end cards' },
  { category: 'photo', label: 'Photos' },
]

/** Uploaded files, grouped by what they are. Each file can be moved to another group or tied to one brand. */
function UploadsBlock({ clientId, uploads, ed, brands }: { clientId: string; uploads: Doc['uploads']; ed: ReturnType<typeof useSectionEditor<Doc>>; brands: ReturnType<typeof useBrands> }) {
  const { jobs, upload, remove } = useUploads(clientId)
  const { role, can } = useAuth()
  const mode = useMode()
  const [urls, setUrls] = useState<Record<string, string>>({})
  const ids = Object.keys(uploads)
  useEffect(() => {
    for (const id of ids) {
      if (urls[id]) continue
      downloadUrl(uploads[id]!.storagePath).then((u) => setUrls((x) => ({ ...x, [id]: u }))).catch(() => undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')])
  const categoryOf = (id: string) => (uploads[id]!.category as UploadCategory | null) ?? guessUploadCategory(uploads[id]!.name, uploads[id]!.contentType)
  const byCategory = new Map<UploadCategory, string[]>()
  for (const id of ids.sort((a, b) => (uploads[b]!.at ?? 0) - (uploads[a]!.at ?? 0))) {
    const c = categoryOf(id)
    byCategory.set(c, [...(byCategory.get(c) ?? []), id])
  }
  const canEdit = mode !== 'preview'
  const categoryOptions = UPLOAD_CATEGORIES.map((c) => ({ value: c.key, label: c.label }))
  const brandOptions = [{ value: ANY_BRAND, label: 'All brands' }, ...brands.order.map((b) => ({ value: b, label: brands.name(b) }))]
  return (
    <section className="grid gap-6 border-t border-ink pt-6">
      <div className="grid max-w-form gap-1">
        <Eyebrow tone="muted">Files</Eyebrow>
        <h2 className="text-h3">Upload directly</h2>
        <p className="text-small text-dim">Drop in whatever you have. We sort it by type, so there is nothing to label. Images, video, PDF, ZIP, fonts and AI/EPS, up to 100 MB each.</p>
      </div>
      <div className="grid max-w-form gap-2">
        <p className="text-eyebrow uppercase tracking-eyebrow text-ink">What helps most</p>
        <ul className="grid gap-1.5 text-small">
          {LOOK_FOR.map((l) => {
            const n = byCategory.get(l.category)?.length ?? 0
            return (
              <li key={l.category} className="flex items-center gap-2.5">
                <span aria-hidden="true" className={clsx('grid h-4 w-4 shrink-0 place-items-center border', n ? 'border-ok bg-ok text-paper' : 'border-gray-400')}>
                  {n > 0 && <Icons.Check size={10} />}
                </span>
                <span className={n ? 'text-ink' : 'text-charcoal'}>{l.label}</span>
                <span className="text-dim tabular-nums">{n ? `${n} uploaded` : ''}</span>
                <span className="sr-only">{n ? '' : 'Not uploaded'}</span>
              </li>
            )
          })}
        </ul>
        <p className="text-small text-dim">Already in a shared folder? That counts; no need to upload it again.</p>
      </div>
      <Uploader onFiles={upload} jobs={jobs} accept="image/*,video/*,.pdf,.zip,.ai,.eps,.otf,.ttf,.woff,.woff2" hint="Up to 100 MB per file" className="max-w-form" />
      {UPLOAD_CATEGORIES.filter((c) => byCategory.has(c.key)).map((c) => (
        <div key={c.key} className="grid max-w-form gap-1">
          <p className="text-eyebrow uppercase tracking-eyebrow text-ink">
            {c.label} <span className="ml-1 text-dim tabular-nums">{byCategory.get(c.key)!.length}</span>
          </p>
          <ul className="divide-y divide-gray-200 border-y border-gray-200">
            {byCategory.get(c.key)!.map((id) => (
              <UploadRow
                key={id}
                file={uploads[id]!}
                href={urls[id]}
                category={c.key}
                onCategory={(v) => ed.setRaw(`uploads.${id}.category`, v)}
                onBrand={(v) => ed.setRaw(`uploads.${id}.brandId`, v)}
                onNote={(v) => ed.setRaw(`uploads.${id}.note`, v)}
                onRemove={role === 'staff' && can('clients.edit') ? () => void remove(id, uploads[id]!.storagePath) : undefined}
                editable={canEdit}
                brandOptions={brands.order.length > 1 ? brandOptions : null}
                categoryOptions={categoryOptions}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

/** One file: its group and brand pickers, and an optional note shown as a footnote ("Primary logo"). */
function UploadRow({ file, href, category, onCategory, onBrand, onNote, onRemove, editable, brandOptions, categoryOptions }: {
  file: Doc['uploads'][string]
  href?: string
  category: UploadCategory
  onCategory: (v: string) => void
  onBrand: (v: string | null) => void
  onNote: (v: string | null) => void
  onRemove?: () => void
  editable: boolean
  brandOptions: { value: string; label: string }[] | null
  categoryOptions: { value: string; label: string }[]
}) {
  const [noteOpen, setNoteOpen] = useState(false)
  const noteRef = useRef<HTMLInputElement>(null)
  const note = file.note ?? ''
  const showNote = editable && (noteOpen || note !== '')
  return (
    <UploadedFileRow
      name={file.name}
      size={file.size}
      href={href}
      onRemove={onRemove}
      controls={
        editable && (
          <>
            <Select<string> size="sm" aria-label={`Group for ${file.name}`} value={category} onValueChange={onCategory} options={categoryOptions} />
            {brandOptions && (
              <Select<string> size="sm" aria-label={`Brand for ${file.name}`} value={file.brandId ?? ANY_BRAND} onValueChange={(v) => onBrand(v === ANY_BRAND ? null : v)} options={brandOptions} />
            )}
            {!showNote && (
              <button
                type="button"
                onClick={() => {
                  setNoteOpen(true)
                  requestAnimationFrame(() => noteRef.current?.focus())
                }}
                className="text-small text-dim underline decoration-gray-300 underline-offset-4 hover:text-ink hover:decoration-ink"
              >
                + Add a note
              </button>
            )}
          </>
        )
      }
      footnote={
        showNote ? (
          <label className="flex items-baseline gap-2 text-small">
            <span className="shrink-0 text-dim">Note</span>
            <input
              ref={noteRef}
              value={note}
              onChange={(e) => onNote(e.target.value)}
              onBlur={() => {
                if (!note.trim()) {
                  if (file.note != null) onNote(null)
                  setNoteOpen(false)
                }
              }}
              placeholder="e.g. Primary logo, or white version for dark backgrounds"
              aria-label={`Note on ${file.name}`}
              className="min-w-0 flex-1 border-b border-rule bg-transparent py-1 text-ink placeholder:text-dim focus:border-ink focus:outline-none"
            />
          </label>
        ) : (
          !editable && note && <p className="text-small text-charcoal"><span className="text-dim">Note</span> {note}</p>
        )
      }
    />
  )
}

/** Select values cannot be empty, so "no particular brand" needs a stand-in. */
const ANY_BRAND = '__all'
